import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useEligibleStorages } from './useEligibleStorages';

const fieldToApiMapping: Record<string, string> = {
  storage_date: 'storage_date',
  quantity: 'quantity',
  total_volume_m3: 'total_volume_m3',
  preinvoice_id: 'preinvoice_id',
  invoice_id: 'invoice_id',
  period: 'period',
};

export const useEligibleStorageMain = () => {
  const { params: queryParams, updateParams: setQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
  });
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.search as string);
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? (queryParams.filters as FilterConditionOutput[]) : [],
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const searchFilter = useMemo(() => {
    if (!filterConditions || filterConditions.length === 0) {
      return undefined;
    }

    const filters: any[] = [];

    filterConditions.forEach((condition) => {
      if (!condition.action) {
        return;
      }

      const apiField = fieldToApiMapping[condition.column] || condition.column;
      const operator = condition.action;

      if (operator === 'is_null' || operator === 'is_not_null') {
        filters.push({
          column: apiField,
          operator,
        });
        return;
      }

      if (condition.value === undefined || condition.value === null || condition.value === '') {
        return;
      }

      let value = condition.value;
      if (operator === 'in' || operator === 'not_in') {
        if (!Array.isArray(value)) {
          if (typeof value === 'string') {
            value = value
              .split(',')
              .map((item) => String(item).trim())
              .filter(Boolean);
          } else {
            value = [value];
          }
        }

        if (!Array.isArray(value) || value.length === 0) {
          return;
        }
      }

      filters.push({
        column: apiField,
        operator,
        value,
      });
    });

    if (filters.length === 0) {
      return undefined;
    }

    return {
      filters,
      logic: 'AND',
    };
  }, [filterConditions]);

  const normalizedSearch = useMemo(() => {
    const value = searchTerm?.trim();
    return value ? value : undefined;
  }, [searchTerm]);

  const { storages, isLoading, isFetching, error, total, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEligibleStorages({
      searchFilter,
      search: normalizedSearch,
      sortBy: 'storage_date',
      sortDirection: 'desc',
    });

  const hasActiveFilters = useMemo(() => filterConditions.length > 0, [filterConditions]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      setQueryParams({
        filters: conditions,
      });
    },
    [setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setQueryParams({
        search: value,
      });
    },
    [setQueryParams],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  return {
    storages,
    isLoading,
    isFetching,
    error,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
    filterConditions,
    searchTerm,
    selectedColumns,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
  };
};
